// ============================================================
// Viashift — Supabase client (Phase 1A.5, auth options 1B.1)
// ============================================================
// Singleton typed client for the browser. Reads anon credentials
// from Vite env and exposes a `Database`-typed client so query
// builders (`.from('products').select(...)`) return correct types.
//
// The VITE_ prefix is required for env vars to reach the browser
// bundle. Anything without it stays server-side — exactly what we
// want for service-role keys (which never belong in this file).
//
// Env vars are validated at module load. If they're missing we fail
// loudly rather than waiting for the first network call to return a
// confusing 401.
//
// Auth options (added in 1B.1):
//   - flowType: 'pkce'          — PKCE (Proof Key for Code Exchange)
//                                 is the modern magic-link flow.
//                                 Callback URL receives `?code=...`
//                                 which we exchange for a session
//                                 via supabase.auth.exchangeCodeForSession.
//                                 More secure than the legacy implicit
//                                 flow and explicit so future supabase-js
//                                 default changes can't flip us silently.
//   - storageKey: 'viashift-auth' — Belt-and-suspenders for Lucix
//                                 isolation on top of the port pin.
//                                 Even if two apps somehow ended up
//                                 on the same localhost port, the
//                                 localStorage keys wouldn't collide.
//                                 See DECISIONS.md 2026-04-23 entry.
//
// Edge Functions construct their own service-role clients —
// this file is browser-only.
// ============================================================

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url) {
  throw new Error(
    'VITE_SUPABASE_URL is not set. Copy .env.example to .env.local and fill it in.',
  );
}
if (!anonKey) {
  throw new Error(
    'VITE_SUPABASE_ANON_KEY is not set. Copy .env.example to .env.local and fill it in.',
  );
}

export const supabase: SupabaseClient<Database> = createClient<Database>(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storageKey: 'viashift-auth',
  },
});
